import { WorkerStage } from "@clipboard-health/contract-worker-app-bff";
import {
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Title,
  useModalState,
} from "@clipboard-health/ui-components";
import { getTheme } from "@clipboard-health/ui-theme";
import { Stack, ThemeProvider } from "@mui/material";
// eslint-disable-next-line no-restricted-imports
import { UPDATE_PROFILE_WORKFLOW } from "@src/app/profile/profile/constants";
// eslint-disable-next-line no-restricted-imports
import { UpdateProfileImage } from "@src/app/profile/profile/profileImage/updateImage";
import {
  resetAgentProfilePictureQuery,
  useWorkerProfilePicture,
} from "@src/appV2/Agents/api/useWorkerProfilePicture";
import { DeprecatedGlobalAppV1Paths } from "@src/appV2/App/paths";
import { formatUsPhoneNumber, FullScreenDialog } from "@src/appV2/lib";
import { APP_V2_APP_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { ACCOUNTS_BASE_PATH, DELETE_ACCOUNT_FULL_PATH } from "@src/appV2/redesign/Accounts/paths";
import { useIsAccountPageRedesignEnabled } from "@src/appV2/redesign/Accounts/useIsAccountPageRedesignEnabled";
import { PageWrapper } from "@src/appV2/redesign/components/PageWrapper";
import { StickyScrollPageHeader } from "@src/appV2/redesign/components/StickyScrollPageHeader";
import { INDEPENDENT_CONTRACTOR_BADGE_FULL_PATH } from "@src/appV2/redesign/Documents/paths";
import { AnalyticsOpenFromValues } from "@src/appV2/redesign/IndependantContractorBadge/constants";
import { PageContent } from "@src/appV2/redesign/WorkerShifts/components/PageContent";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { USER_EVENTS } from "@src/constants";
import { useQueryClient } from "@tanstack/react-query";
import { useRef } from "react";
import { useHistory } from "react-router-dom";

import { Button } from "../components/Button";
import { ProfileImage } from "../WorkWithFriends/components/ProfileImage";

export function ProfilePage() {
  const worker = useDefinedWorker();
  const history = useHistory();
  const scrollRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();
  const updateProfileImageModalState = useModalState();

  useLogEffect(USER_EVENTS.VIEWED_PROFILE);

  const updateEmail = () => {
    logEvent(USER_EVENTS.PROFILE_CHANGE_STARTED, {
      type: "EMAIL",
    });
    localStorage.setItem("profileUpdateWorkflow", UPDATE_PROFILE_WORKFLOW.UPDATE_EMAIL);
    history.push(DeprecatedGlobalAppV1Paths.UPDATE_PROFILE);
  };

  const updatePhone = () => {
    logEvent(USER_EVENTS.PROFILE_CHANGE_STARTED, {
      type: "PHONE",
    });
    localStorage.setItem("profileUpdateWorkflow", UPDATE_PROFILE_WORKFLOW.UPDATE_PHONE);
    history.push(DeprecatedGlobalAppV1Paths.UPDATE_PROFILE);
  };

  const workerHasRequestedDeletion = worker.stage === WorkerStage.DELETION_REQUESTED;

  const { data: workerProfileImageUrl } = useWorkerProfilePicture(worker.userId);

  const isAccountPageRedesignEnabled = useIsAccountPageRedesignEnabled();

  return (
    <ThemeProvider theme={getTheme()}>
      <PageWrapper variant="tertiary">
        <StickyScrollPageHeader
          title="Profile"
          scrollContainerRef={scrollRef}
          fallbackGoBackPath={ACCOUNTS_BASE_PATH}
          headerVariant="tertiary"
        />

        <PageContent ref={scrollRef}>
          <Stack spacing={2}>
            <Stack
              spacing={6}
              alignItems="center"
              sx={{
                paddingY: 6,
              }}
            >
              <Title semibold variant="h4" component="h2" sx={{ textAlign: "center" }}>
                {worker.name}
              </Title>
              <ProfileImage
                size="large"
                firstName={worker.firstName ?? ""}
                lastName={worker.lastName ?? ""}
                src={workerProfileImageUrl?.distribution_url}
              />
              <Button
                startIconType="pencil"
                variant="contained"
                onClick={() => {
                  updateProfileImageModalState.openModal();
                }}
              >
                Upload Photo
              </Button>
            </Stack>

            <List
              component="div"
              sx={{
                "& li": {
                  height: 62,
                },
              }}
            >
              <ListItem divider>
                <ListItemButton onClick={updateEmail}>
                  <ListItemIcon iconType="user" />
                  <ListItemText primary="Email" secondary={worker.email} />
                </ListItemButton>
              </ListItem>

              <ListItem divider>
                <ListItemButton onClick={updatePhone}>
                  <ListItemIcon iconType="phone" />
                  <ListItemText primary="Phone" secondary={formatUsPhoneNumber(worker.phone)} />
                </ListItemButton>
              </ListItem>

              <ListItem divider>
                <ListItemButton
                  onClick={() => {
                    history.push(DeprecatedGlobalAppV1Paths.ADDRESS);
                  }}
                >
                  <ListItemIcon iconType="map-pin" />
                  <ListItemText
                    primary="Home Address"
                    secondary="Set your home address to see nearby shifts"
                  />
                </ListItemButton>
              </ListItem>

              <ListItem divider>
                <ListItemButton
                  onClick={() => {
                    logEvent(APP_V2_APP_EVENTS.INDEPENDENT_CONTRACTOR_BADGE_OPENED, {
                      workerId: worker.userId,
                      workerFullName: `${worker.firstName} ${worker.lastName}`,
                      openedFrom: AnalyticsOpenFromValues.PROFILE,
                    });
                    history.push(INDEPENDENT_CONTRACTOR_BADGE_FULL_PATH);
                  }}
                >
                  <ListItemIcon iconType="clipboard" />
                  <ListItemText primary="Work Badge" secondary="View and download your badge" />
                </ListItemButton>
              </ListItem>

              {!isAccountPageRedesignEnabled && (
                <ListItem divider>
                  <ListItemButton
                    onClick={() => {
                      history.push(DeprecatedGlobalAppV1Paths.PROFILE_CONTRACT);
                    }}
                  >
                    <ListItemIcon iconType="document" />
                    <ListItemText
                      primary="Contractor Agreement"
                      secondary="View your contractor agreement"
                    />
                  </ListItemButton>
                </ListItem>
              )}

              {!isAccountPageRedesignEnabled && (
                <ListItem divider>
                  <ListItemButton
                    disabled={workerHasRequestedDeletion}
                    onClick={() => {
                      history.push(DELETE_ACCOUNT_FULL_PATH);
                    }}
                  >
                    <ListItemIcon iconType="remove" />
                    <ListItemText
                      primary={
                        workerHasRequestedDeletion
                          ? "Account Deletion Request Pending"
                          : "Delete Account"
                      }
                      secondary={
                        workerHasRequestedDeletion
                          ? "Your request to delete your account is being processed"
                          : "Delete your account permanently"
                      }
                    />
                  </ListItemButton>
                </ListItem>
              )}
            </List>
          </Stack>
        </PageContent>
        <FullScreenDialog modalState={updateProfileImageModalState}>
          <UpdateProfileImage
            profileUrl={workerProfileImageUrl?.distribution_url ?? ""}
            onClose={() => {
              resetAgentProfilePictureQuery(queryClient, { workerId: worker.userId });
              updateProfileImageModalState.closeModal();
            }}
          />
        </FullScreenDialog>
      </PageWrapper>
    </ThemeProvider>
  );
}
